import React, {useRef, useState, ReactNode} from "react";
import {NavHashLink} from "react-router-hash-link";
import {Link, useParams, useLocation} from "react-router-dom";
import {css, cx} from "@emotion/css";
import html2canvas from "html2canvas";
import qs from "qs";
import axios from "axios";
import {capitalize} from "lodash";
import {Icon} from "@reside/ui";
import {UserPermissionEnum} from "../../utils/types/user";

import {UseIsInViewport} from "../../hooks/useIsInViewport";
import {
  AnyDataPath,
  Facility,
  IFromTo,
} from "../../store/reporting/reporting.common";
import {BLOCK_PERMISSIONS} from "../../pages/page-reporting/permissions.helpers";
import {createActions} from "../../store/reporting-reports/reporting-reports.actions";
import {
  isReportsDataPath,
  PER_PAGE,
  ReportsDataPath,
} from "../../store/reporting-reports/reporting-reports.common";
import {dispatch} from "../../store";
import {RequiredUserPermissionsSome} from "../admin-user-permissions/RequiredUserPermissionsSome";
import {TemporaryHideChart} from "../../pages/page-reporting/TemporaryHideChart";
import {ReportContextConsumer} from "../../pages/page-reporting/ReportContext";
import {ChartTypes} from "../../pages/page-reporting/constants";
import {FileExtensionItem} from "./file-export-dropdown-select/FileExportDropdownSelect";
import {Header} from "./Header";
import {
  downloadCSV,
  downloadPDF,
  downloadXLSX,
  FileExtension,
} from "./downloadExport";
import {linkBack} from "./ReportingBlock.helpers";
import {
  Column,
  ReportWrapper,
  ReportBody,
  ReportFooter,
} from "./ReportingBlock.styles";
import {UrlProps} from "./ReportingBlock.types";

import "./ReportingBlock.scss";

export type {UrlProps};

export type ReportingBlockSetters = Readonly<{
  setExportDataPath: (exportDataPath: AnyDataPath) => void;
  exportCanvasToPDF: (canvas: HTMLCanvasElement) => void;
  half?: boolean;
  isFullReport?: boolean;
  enabled: boolean;
}>;

type OwnProps = Readonly<{
  chartId: string;
  chartType?: ChartTypes;
  children: (reportingBlockSetters: ReportingBlockSetters) => ReactNode;
  dateRange: IFromTo;
  facilities: Facility[];
  half?: boolean;
  setPageLoading?: (state: boolean) => void;
  overview?: boolean;
  hideSeeFullReportLink?: boolean;
  permissionInChart?: ReadonlyArray<UserPermissionEnum>;
  isDynamicCheckbox?: boolean;
}>;

type Props = OwnProps & {urlProps?: UrlProps};

type FullReportLinkProps = {
  fullReportLink: string;
  facilities: Facility[];
  overview: boolean | undefined;
  locationPathname: string;
};

const FullReportLink = ({
  fullReportLink,
  facilities,
  overview,
  locationPathname,
}: FullReportLinkProps) => (
  <ReportFooter>
    <Link
      to={{
        pathname: fullReportLink,
        search: qs.stringify({facilities}, {arrayFormat: "brackets"}),
        state: {fromOverview: overview, previousUrl: locationPathname},
      }}
    >
      See full report <Icon name="right" size={8} />
    </Link>
  </ReportFooter>
);

type ReportingBlockContentProps = {
  inViewRef: React.Ref<HTMLDivElement>;
  wasInViewport: boolean;
  bodyRef: React.RefObject<HTMLDivElement>;
  isFullReport: boolean;
  urlProps: UrlProps | undefined;
  location: ReturnType<typeof useLocation>;
  half: boolean | undefined;
  disableHalf: boolean;
  getExportDataPath: () => AnyDataPath;
  onExport: (item: FileExtensionItem) => void;
  hideSeeFullReportLink: boolean;
  fullReportLink: string | undefined;
  facilities: Facility[];
  overview: boolean | undefined;
  children: OwnProps["children"];
  setExportDataPath: (path: AnyDataPath) => void;
  exportCanvasToPDF: (canvas: HTMLCanvasElement) => void;
};

const ReportingBlockContent = ({
  inViewRef,
  wasInViewport,
  bodyRef,
  isFullReport,
  urlProps,
  location,
  half,
  disableHalf,
  getExportDataPath,
  onExport,
  hideSeeFullReportLink,
  fullReportLink,
  facilities,
  overview,
  children,
  setExportDataPath,
  exportCanvasToPDF,
}: ReportingBlockContentProps) => (
  <>
    {isFullReport && (
      <NavHashLink
        to={linkBack(urlProps, location)}
        className={css`
          display: block;
          text-decoration: none;
          margin-bottom: 16px;
        `}
      >
        <Icon name="left" size={10} />
        Back to {capitalize(urlProps?.type || "")}
      </NavHashLink>
    )}
    <Column className={cx({half: half && !disableHalf && !isFullReport})}>
      <ReportWrapper>
        <Header dataPath={getExportDataPath()} onExport={onExport} />
        <ReportBody>
          <div ref={inViewRef}>
            <div ref={bodyRef}>
              {(children as any)({
                setExportDataPath,
                exportCanvasToPDF,
                isFullReport,
                half,
                enabled: wasInViewport,
              })}
            </div>
          </div>
        </ReportBody>
        {!isFullReport && !hideSeeFullReportLink && fullReportLink && (
          <FullReportLink
            fullReportLink={fullReportLink}
            facilities={facilities}
            overview={overview}
            locationPathname={location.pathname}
          />
        )}
      </ReportWrapper>
    </Column>
  </>
);

export const ReportingBlockRenderer = ({
  chartId,
  chartType = ChartTypes.reports,
  children,
  dateRange,
  facilities,
  half,
  setPageLoading,
  overview,
  hideSeeFullReportLink = false,
  permissionInChart = [],
  isDynamicCheckbox = false,
  urlProps: urlPropsProp,
}: Props) => {
  const location = useLocation();
  const params = useParams<UrlProps>();
  const urlProps = urlPropsProp || params;

  const [exportDataPath, setExportDataPath] =
    useState<AnyDataPath | undefined>();
  const [disableHalf, setDisableHalf] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);

  // Refs prevent stale closures in async callbacks (getCanvasFromReport, downloadPDF)
  const setPageLoadingRef = useRef(setPageLoading);
  const facilitiesRef = useRef(facilities);
  const exportDataPathRef = useRef<AnyDataPath | undefined>();
  setPageLoadingRef.current = setPageLoading;
  facilitiesRef.current = facilities;
  exportDataPathRef.current = exportDataPath;

  const getExportDataPath = () =>
    ({
      ...exportDataPathRef.current,
      query: {
        ...exportDataPathRef.current?.query,
        facilities: facilitiesRef.current,
      },
    } as AnyDataPath);

  const getCanvasFromReport = () => {
    setDisableHalf(true);
    const exportElement = bodyRef.current as HTMLElement;
    const parentContainer = exportElement.parentElement as HTMLElement;
    const button = parentContainer.getElementsByTagName("button")[0];

    if (button) {
      button.style.display = "none";
    }

    parentContainer.style.overflow = "hidden";
    parentContainer.style.width = `${exportElement.clientWidth}px`;
    exportElement.style.width = "1280px";

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // setTimeout is used to wait for rerendering graphs to full width
        html2canvas(exportElement, {
          windowWidth: 1920,
          windowHeight: 1080,
          scale: 2,
          logging: false,
          onclone: document => {
            setDisableHalf(false);
            exportElement.style.width = "";
            parentContainer.style.width = "";
            parentContainer.style.overflow = "";
            // this extra style expands scrollable content
            const head = document.head;
            const style = document.createElement("style");

            style.type = "text/css";
            style.appendChild(
              document.createTextNode(
                ".chart-header { align-items: stretch !important; }" +
                  ".graph-bar-wrapper { max-height: none !important; } " +
                  ".list-table-reporting-wrapper { max-height: none !important; } " +
                  ".list-table-reporting tbody { height: auto !important; }" +
                  ".list-table-reporting-full+a { display: none; }" +
                  ".reporting-block__body { padding: 0 !important; }" +
                  ".fakeCheck { font-weight: bold; font-size: 11px; }" +
                  ".ellipsis span { display: inline-block !important; overflow: visible !important; white-space: normal !important; }" +
                  ".graph-bar tbody { height: auto !important; }" +
                  ".additional-data { max-height: none !important; }",
              ),
            );
            head.appendChild(style);

            const allChecks = document.querySelectorAll(
              ".list-table-reporting td .icon-check",
            );

            allChecks.forEach(item => {
              const fakeCheck = document.createElement("span");

              fakeCheck.innerHTML = "&#10004;";
              fakeCheck.className = "fakeCheck";
              item.parentNode.replaceChild(fakeCheck, item);
            });
          },
        })
          .then((canvas: any) => {
            resolve(canvas);
            if (button) {
              button.style.display = "flex";
            }
          })
          .catch((error: any) => {
            reject(error);
            if (button) {
              button.style.display = "flex";
            }
          });
      }, 0);
    });
  };

  const exportCanvasToPDF = (canvas: HTMLCanvasElement) => {
    downloadPDF(canvas, getExportDataPath()).then(() => {
      setPageLoadingRef.current?.(false);
    });
  };

  const onPDFExport = () => {
    const exportDataPathValue = getExportDataPath();
    const actions = createActions(exportDataPathValue as ReportsDataPath);

    setPageLoadingRef.current?.(true);

    if (isReportsDataPath(exportDataPathValue)) {
      /**
       * If we have full report with load more in "Reports page" we need to load all data to DOM.
       */
      dispatch(actions.loadAllForPDF()).then((oldPage: number) => {
        getCanvasFromReport().then(canvas => {
          const cancelTokenSource = axios.CancelToken.source();

          dispatch(
            actions.fetchReports(
              cancelTokenSource,
              0,
              (oldPage + 1) * PER_PAGE,
              true,
            ),
          );
          downloadPDF(canvas as HTMLCanvasElement, getExportDataPath()).then(
            () => {
              setPageLoadingRef.current?.(false);
            },
          );
        });
      });
    } else {
      getCanvasFromReport().then(canvas => {
        downloadPDF(canvas as HTMLCanvasElement, getExportDataPath()).then(
          () => {
            setPageLoadingRef.current?.(false);
          },
        );
      });
    }
  };

  const onExport = ({id: extension}: FileExtensionItem) => {
    if (extension === FileExtension.PDF) {
      onPDFExport();
    } else if (extension === FileExtension.CSV) {
      downloadCSV(getExportDataPath());
    } else if (extension === FileExtension.XLSX) {
      downloadXLSX(getExportDataPath());
    }
  };

  const linkToFullReport = () => {
    const baseUrl = location.pathname
      .replace(/\/+$/, "")
      .replace("/admin/reporting/", `/admin/reporting/${chartType}/`);

    return dateRange?.from && dateRange.to
      ? [baseUrl, chartId, dateRange.from, dateRange.to].join("/")
      : undefined;
  };

  const isFullReport = Boolean(
    urlProps?.chartId && urlProps.chartId === chartId,
  );

  if (urlProps?.chartId && urlProps.chartId !== chartId) {
    return null;
  }

  const fullReportLink = linkToFullReport();

  return (
    <RequiredUserPermissionsSome
      permissions={
        isDynamicCheckbox
          ? BLOCK_PERMISSIONS.CHECKLIST_ITEM_REPORT_ID
          : BLOCK_PERMISSIONS[chartId] || permissionInChart
      }
    >
      <UseIsInViewport>
        {({ref, wasInViewport}) => (
          <ReportingBlockContent
            inViewRef={ref}
            wasInViewport={wasInViewport}
            bodyRef={bodyRef}
            isFullReport={isFullReport}
            urlProps={urlProps}
            location={location}
            half={half}
            disableHalf={disableHalf}
            getExportDataPath={getExportDataPath}
            onExport={onExport}
            hideSeeFullReportLink={hideSeeFullReportLink}
            fullReportLink={fullReportLink}
            facilities={facilities}
            overview={overview}
            setExportDataPath={setExportDataPath}
            exportCanvasToPDF={exportCanvasToPDF}
          >
            {children}
          </ReportingBlockContent>
        )}
      </UseIsInViewport>
    </RequiredUserPermissionsSome>
  );
};

export const ReportingBlock = ReportingBlockRenderer;

// TODO: remove once the ReportingBlock consumes the ReportContext
export const ConnectedReportingBlock = (
  props: Omit<OwnProps, "dateRange" | "facilities" | "setPageLoading">,
) => (
  <TemporaryHideChart chartIds={[props.chartId]}>
    <ReportContextConsumer>
      {({baseDateRange, activeFacilitiesIds, setPageLoading}) => (
        <ReportingBlock
          dateRange={baseDateRange}
          facilities={activeFacilitiesIds}
          setPageLoading={setPageLoading}
          {...props}
        />
      )}
    </ReportContextConsumer>
  </TemporaryHideChart>
);
